/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { Box, Tooltip, makeStyles } from '@material-ui/core';
import { useMembers, useSpace } from '@ably/spaces/react';
import type { SpaceMember } from '@ably/spaces';
import { getInitials } from 'Utils/getInitials';
import { RfqUserAvatar } from 'pages/auto-rfq/components/user-avatar';
import { useRFQStore } from 'pages/auto-rfq/store';
import type { RfqMemberProfileData } from 'lib/realtime/RfqSpacesProvider';

const MAX_VISIBLE = 5;

const useStyles = makeStyles(() => ({
  stack: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8
  },
  item: {
    display: 'inline-flex',
    marginLeft: -8
  },
  // Color/image come from SAvatar; this only adds the pile chrome.
  avatar: {
    border: '2px solid #FFFFFF',
    fontSize: 11,
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
  },
  self: {
    outline: '2px solid #1E96FC',
    outlineOffset: 1
  },
  overflow: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '2px solid #FFFFFF',
    background: '#5D7083',
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: -8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
  }
}));

const initialsFor = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface MemberAvatarProps {
  member: SpaceMember;
  classes: ReturnType<typeof useStyles>;
  isSelf?: boolean;
  // Whether this member's current row is navigable (the parent resolved a
  // target it knows how to jump to). Clicking calls `onSelect`.
  hasTarget?: boolean;
  onSelect?: () => void;
}

const MemberAvatar = ({
  member,
  classes,
  isSelf,
  hasTarget,
  onSelect
}: MemberAvatarProps) => {
  const profile = ((member.profileData as unknown) ||
    {}) as RfqMemberProfileData;
  const name = profile.name || member.clientId || 'User';
  // Same initials source the app's SAvatar uses, so the string-to-color hue
  // matches what this user sees elsewhere; fall back to a name-derived guess.
  const initials =
    getInitials({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email
    }) || initialsFor(name);

  const clickable = !isSelf && !!hasTarget && !!onSelect;
  const tooltipLabel = isSelf
    ? `${name} (You)`
    : `${name}${clickable ? ' — go to their row' : ''}`;

  return (
    <Tooltip title={tooltipLabel} arrow>
      <Box
        component="span"
        className={classes.item}
        onClick={clickable ? () => onSelect?.() : undefined}
        style={{ cursor: clickable ? 'pointer' : 'default' }}>
        <RfqUserAvatar
          size="sm"
          userId={profile.userId}
          value={initials}
          className={`${classes.avatar} ${isSelf ? classes.self : ''}`}
        />
      </Box>
    </Tooltip>
  );
};

/**
 * Avatar pile of everyone currently in this RFQ's Space — self first with a
 * subtle ring, then others. Showing self gives a visible signal that the
 * space initialized even when no other tab is open.
 *
 * Disconnected members fall off after the SpaceProvider's offlineTimeout
 * (120s in RfqSpacesProvider) — the SDK handles that without us filtering.
 */
/** A resolved navigation target: which row a member is on, and its surface. */
type MemberTarget = { id: string; kind: 'rfq' | 'quote' };

const targetFromLocation = (
  loc: { rfqItemId?: string; quoteItemId?: string } | null | undefined
): MemberTarget | null => {
  if (loc?.quoteItemId) return { id: loc.quoteItemId, kind: 'quote' };
  if (loc?.rfqItemId) return { id: loc.rfqItemId, kind: 'rfq' };
  return null;
};

interface PresenceStackProps {
  /**
   * Navigate to a quote item when following a collaborator on the Quote tab.
   * Provided by the Quotes V2 page; absent on the standalone Auto RFQ page,
   * where quote-tab locations aren't navigable.
   */
  onNavigateQuoteItem?: (quoteItemId: string) => void;
  /**
   * Navigate to an RFQ item when following a collaborator on the Product Match
   * grid. The Quotes V2 page wires this to switch to the Product Match tab and
   * scroll; when omitted (standalone Auto RFQ, where the grid is always
   * mounted) it falls back to `requestScrollToRfqItem`.
   */
  onNavigateRfqItem?: (rfqItemId: string) => void;
}

export const PresenceStack = ({
  onNavigateQuoteItem,
  onNavigateRfqItem
}: PresenceStackProps) => {
  const classes = useStyles();
  const { space } = useSpace();
  const { self, others, connectionError, channelError } = useMembers();
  const requestScrollToRfqItem = useRFQStore(s => s.requestScrollToRfqItem);

  // Remember the last row each member was actually on, keyed by connectionId.
  // The broadcast location is *live* (cleared when a member moves off all rows
  // so the inline row chip cleans up), so for navigation we keep the last
  // non-null value here — falling back to `previousLocation` so the "moved
  // off" clear still records where they just were. This also sidesteps the
  // stale-`others` pitfall useRowFocus documents. A member's location carries
  // either `rfqItemId` (RFQ grid / Product Match) or `quoteItemId` (Quote tab).
  const [lastLocations, setLastLocations] = useState<
    Record<string, MemberTarget>
  >({});

  useEffect(() => {
    if (!space) return undefined;
    const handler = (update: unknown) => {
      const u = update as {
        member?: { connectionId?: string };
        currentLocation?: { rfqItemId?: string; quoteItemId?: string } | null;
        previousLocation?: { rfqItemId?: string; quoteItemId?: string } | null;
      };
      const connId = u?.member?.connectionId;
      const target =
        targetFromLocation(u?.currentLocation) ??
        targetFromLocation(u?.previousLocation);
      if (!connId || !target) return;
      setLastLocations(prev =>
        prev[connId]?.id === target.id && prev[connId]?.kind === target.kind
          ? prev
          : { ...prev, [connId]: target }
      );
    };
    space.locations.subscribe('update', handler);
    return () => {
      try {
        space.locations.unsubscribe('update', handler);
      } catch (err) {
        console.warn('[spaces] locations.unsubscribe failed', err);
      }
    };
  }, [space]);

  // Prefer the remembered last row; fall back to the member's current location
  // for members already on a row before we subscribed.
  const targetFor = (member: SpaceMember): MemberTarget | undefined => {
    const connId = member.connectionId;
    return (
      (connId && lastLocations[connId]) ||
      targetFromLocation(member.location as any) ||
      undefined
    );
  };

  // A target is navigable from here if it's an RFQ row, or a quote row and the
  // host page wired a quote navigator.
  const navigate = (target: MemberTarget) => {
    if (target.kind === 'quote') {
      onNavigateQuoteItem?.(target.id);
    } else if (onNavigateRfqItem) {
      onNavigateRfqItem(target.id);
    } else {
      requestScrollToRfqItem(target.id);
    }
  };
  const canNavigate = (target?: MemberTarget) =>
    !!target && (target.kind === 'rfq' || !!onNavigateQuoteItem);

  // Surface connection / channel errors to console for debugging — these
  // typically indicate a capability mismatch (token missing `publish`) or a
  // failure to attach the underlying channel.
  useEffect(() => {
    if (connectionError) {
      console.warn('Spaces connectionError', connectionError);
    }
  }, [connectionError]);
  useEffect(() => {
    if (channelError) {
      console.warn('Spaces channelError', channelError);
    }
  }, [channelError]);

  const connectedOthers = (others || []).filter(m => m.isConnected);
  if (!self && connectedOthers.length === 0) return null;

  const visibleOthers = connectedOthers.slice(0, MAX_VISIBLE);
  const overflow = connectedOthers.length - visibleOthers.length;

  return (
    <Box className={classes.stack}>
      {self && <MemberAvatar member={self} classes={classes} isSelf />}
      {visibleOthers.map(member => {
        const target = targetFor(member);
        const navigable = canNavigate(target);
        return (
          <MemberAvatar
            key={member.connectionId}
            member={member}
            classes={classes}
            hasTarget={navigable}
            onSelect={navigable && target ? () => navigate(target) : undefined}
          />
        );
      })}
      {overflow > 0 && (
        <Tooltip
          title={connectedOthers
            .slice(MAX_VISIBLE)
            .map(m => (m.profileData as { name?: string })?.name || m.clientId)
            .join(', ')}
          arrow>
          <Box className={`${classes.avatar} ${classes.overflow}`}>
            +{overflow}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
